import { useState } from 'react'

import { INSTALLED_KEY, updateServiceWorker, useNeedRefresh } from '../pwa'

/**
 * Sticky bottom banner surfacing "new version available" when the PWA
 * service worker detects an update — i.e. when a new SW is installed and
 * waiting. Tap = full reload onto the new build (skipWaiting +
 * window.location.reload via vite-plugin-pwa). Registration itself lives in
 * the `src/pwa.ts` singleton, shared with the broken-shell recovery path —
 * this component only renders the prompt.
 *
 * Under the current update policy (design.md §8.5) the SW ships with
 * `skipWaiting`, so it never enters the waiting state and this banner stays
 * silent. It is kept mounted so the explicit-update UX self-reactivates if
 * that policy is ever rolled back.
 *
 * First-install guard: the banner must fire ONLY for a genuine update to an
 * already-installed app — never on the very first SW install (opening the
 * link for the first time), where surfacing "ny versjon" for the build you
 * just loaded is nonsensical and alarming mid-event. We snapshot a
 * localStorage marker at mount (before registration sets it): if the app was
 * already installed on this device in a prior session, a `needRefresh` is a
 * real update and we show the banner; if not, this is the first install and
 * we stay silent while the registration records the marker for next time.
 *
 * Positioning: floats above BottomNav using the same `--bottom-nav-h`
 * + safe-area inset that the main scroll area uses, so the banner
 * never hides nav tabs or gets clipped by an iPhone home bar.
 */
const UpdatePrompt = () => {
  const [installedBefore] = useState(() => {
    // Canonical first-install signal: an already-installed app's page is
    // controlled by its service worker from load (`controller` set); a
    // first-ever load is uncontrolled (`controller` null). More reliable than
    // the localStorage marker alone (which can be cleared or storage-blocked),
    // so OR the two. Both are false on a genuinely fresh device, so the update
    // banner still cannot fire on a first load.
    const controlled =
      typeof navigator !== 'undefined' && !!navigator.serviceWorker?.controller
    try {
      return controlled || localStorage.getItem(INSTALLED_KEY) === '1'
    } catch {
      // Private-mode / storage-blocked: fall back to the controller signal
      // alone rather than risk a spurious first-load banner.
      return controlled
    }
  })
  const needRefresh = useNeedRefresh()

  if (!needRefresh || !installedBefore) return null

  return (
    <button
      type="button"
      onClick={() => updateServiceWorker(true)}
      className="fixed left-0 right-0 z-50 bg-green-700 text-paper px-6 py-3 font-display font-semibold text-sm text-center shadow-lg active:bg-green-900 transition-colors"
      style={{
        bottom: `calc(var(--bottom-nav-h) + env(safe-area-inset-bottom))`,
        minHeight: 44,
      }}
    >
      Ny versjon tilgjengelig — trykk for å oppdatere
    </button>
  )
}

export default UpdatePrompt
